import {
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React from "react";
import { SurveyQuestion } from "src/models";
import { DrawerLayout, SmallInputLabel, TagPicker, TextEditor, useDrawer } from "../../../../components";
import { QuestionType, TagType } from "../../../../constants";
import { useSession } from "../../../../hooks";
import {
  useDeleteSurveyQuestion,
  useEditSurveyQuestionDescription,
  useEditSurveyQuestionIsRequired,
  useEditSurveyQuestionName,
  useEditSurveyQuestionTags,
  useEditSurveyQuestionType,
} from "../../../../http";
import { useDestructiveConfirm } from "../../../../utilities";

interface IProps {
  question: SurveyQuestion;
  surveyGuid: string;
}

const SurveyQuestionDrawerContent = ({ question, surveyGuid }: IProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { closeDrawer } = useDrawer();
  const confirm = useDestructiveConfirm();
  const { mutate: deleteSurveyQuestion, isLoading: isDeletingSurveyQuestion } = useDeleteSurveyQuestion();
  const { userId } = useSession();
  const { mutate: editSurveyQuestionName, isLoading: isUpdatingSurveyQuestionName } = useEditSurveyQuestionName();
  const { mutate: editSurveyQuestionDescription, isLoading: isUpdatingSurveyQuestionDescription } =
    useEditSurveyQuestionDescription();
  const { mutate: editSurveyQuestionTags } = useEditSurveyQuestionTags();
  const { mutate: editSurveyQuestionIsRequired, isLoading: isUpdatingSurveyQuestionIsRequired } =
    useEditSurveyQuestionIsRequired();
  const { mutate: editSurveyQuestionType, isLoading: isUpdatingSurveyQuestionType } = useEditSurveyQuestionType();
  const isLinearScale = question?.questionTypeId === QuestionType.LinearScale;
  const isTitleAndDescription = question?.questionTypeId === QuestionType.TitleAndDescription;

  function handleShowSavedNotification() {
    enqueueSnackbar("Settings saved successfully.", { variant: "success" });
  }

  function handleNameChanged(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
    editSurveyQuestionName(
      {
        guid: question.guid,
        surveyGuid: surveyGuid,
        name: e.target.value,
        modifiedByUserId: userId,
      },
      {
        onSuccess: handleShowSavedNotification,
      },
    );
  }

  function handleDescriptionChanged(value: string) {
    editSurveyQuestionDescription(
      {
        guid: question.guid,
        surveyGuid: surveyGuid,
        description: value,
        modifiedByUserId: userId,
      },
      {
        onSuccess: handleShowSavedNotification,
      },
    );
  }

  function handleIsRequiredChanged(e: any) {
    editSurveyQuestionIsRequired(
      {
        guid: question.guid,
        surveyGuid: surveyGuid,
        isRequired: e.target.checked,
        modifiedByUserId: userId,
      },
      {
        onSuccess: handleShowSavedNotification,
      },
    );
  }

  function handleResponseTypeChanged(e: SelectChangeEvent) {
    editSurveyQuestionType(
      {
        guid: question.guid,
        surveyGuid: surveyGuid,
        modifiedByUserId: userId,
        questionTypeId: Number(e.target.value),
        scalePoints: question?.scalePoints,
        includeNotApplicableOption: question?.includeNotApplicableOption,
        minLabel: question?.minLabel,
        maxLabel: question?.maxLabel,
      },
      {
        onSuccess: handleShowSavedNotification,
      },
    );
  }

  function handleScalePointsChanged(e: SelectChangeEvent) {
    editSurveyQuestionType(
      {
        guid: question.guid,
        surveyGuid: surveyGuid,
        modifiedByUserId: userId,
        questionTypeId: question?.questionTypeId,
        scalePoints: Number(e.target.value),
        includeNotApplicableOption: question?.includeNotApplicableOption,
        minLabel: question?.minLabel,
        maxLabel: question?.maxLabel,
      },
      {
        onSuccess: handleShowSavedNotification,
      },
    );
  }

  function handleIncludeOptionChanged(e: SelectChangeEvent) {
    editSurveyQuestionType(
      {
        guid: question.guid,
        surveyGuid: surveyGuid,
        modifiedByUserId: userId,
        questionTypeId: question?.questionTypeId,
        scalePoints: question?.scalePoints,
        includeNotApplicableOption: JSON.parse(e.target.value),
        minLabel: question?.minLabel,
        maxLabel: question?.maxLabel,
      },
      {
        onSuccess: handleShowSavedNotification,
      },
    );
  }

  function handleMinLabelChanged(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
    editSurveyQuestionType(
      {
        guid: question.guid,
        surveyGuid: surveyGuid,
        modifiedByUserId: userId,
        questionTypeId: question?.questionTypeId,
        scalePoints: question?.scalePoints,
        includeNotApplicableOption: question?.includeNotApplicableOption,
        minLabel: e.target.value,
        maxLabel: question?.maxLabel,
      },
      {
        onSuccess: handleShowSavedNotification,
      },
    );
  }

  function handleMaxLabelChanged(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
    editSurveyQuestionType(
      {
        guid: question.guid,
        surveyGuid: surveyGuid,
        modifiedByUserId: userId,
        questionTypeId: question?.questionTypeId,
        scalePoints: question?.scalePoints,
        includeNotApplicableOption: question?.includeNotApplicableOption,
        minLabel: question?.minLabel,
        maxLabel: e.target.value,
      },
      {
        onSuccess: handleShowSavedNotification,
      },
    );
  }

  function handleTagsChanged(value: string[]) {
    editSurveyQuestionTags(
      {
        guid: question.guid,
        surveyGuid: surveyGuid,
        tags: value,
      },
      {
        onSuccess: handleShowSavedNotification,
      },
    );
  }

  const handleDeleteClicked = async () => {
    if (question == null) {
      return;
    }

    const { confirmed } = await confirm({
      title: `Delete Question`,
      content: <Typography sx={{ marginBottom: [1] }}>Are you sure you want to delete this question?</Typography>,
      confirmationText: `Delete Question`,
      confirmationButtonProps: {
        disabled: isDeletingSurveyQuestion,
        endIcon: isDeletingSurveyQuestion && <CircularProgress size={20} />,
      },
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    deleteSurveyQuestion(
      {
        guid: question.guid,
        surveyGuid: surveyGuid,
      },
      {
        onSuccess: () => {
          closeDrawer();
          enqueueSnackbar(`Question deleted successfully.`, { variant: "success" });
        },
      },
    );
  };

  return (
    <DrawerLayout
      title={
        <Typography variant="h6">
          {question.questionTypeId === QuestionType.TitleAndDescription ? "Title & Description" : "Question Details"}
        </Typography>
      }
    >
      <Stack>
        <Stack spacing={1} sx={{
          p: 1
        }}>
          {isTitleAndDescription ? (
            <>
              <Stack>
                <SmallInputLabel>Title</SmallInputLabel>
                <TextField
                  disabled={isUpdatingSurveyQuestionName}
                  onBlur={handleNameChanged}
                  defaultValue={question.name}
                />
              </Stack>
              <Stack>
                <SmallInputLabel>Description</SmallInputLabel>
                <TextEditor
                  readonly={isUpdatingSurveyQuestionDescription}
                  initialValue={question.description}
                  onBlur={handleDescriptionChanged}
                />
              </Stack>
            </>
          ) : (
            <>
              <Stack>
                <SmallInputLabel>Question</SmallInputLabel>
                <TextField
                  disabled={isUpdatingSurveyQuestionName}
                  onBlur={handleNameChanged}
                  defaultValue={question.name}
                />
              </Stack>
              <Stack>
                <SmallInputLabel>Question Description</SmallInputLabel>
                <TextField
                  disabled={isUpdatingSurveyQuestionDescription}
                  multiline
                  onBlur={(e) => handleDescriptionChanged(e.target.value)}
                  defaultValue={question.description}
                />
              </Stack>
              <Stack direction="row" spacing={0.5} sx={{
                alignItems: "center"
              }}>
                <Switch
                  name="required"
                  onChange={handleIsRequiredChanged}
                  disabled={isUpdatingSurveyQuestionIsRequired}
                  checked={question.isRequired}
                />
                <Typography>Required Question</Typography>
              </Stack>
              <Stack>
                <SmallInputLabel>Response Type</SmallInputLabel>
                <Select
                  disabled={isUpdatingSurveyQuestionType}
                  defaultValue={question.questionTypeId.toString()}
                  onChange={handleResponseTypeChanged}
                >
                  <MenuItem value="2">Linear Scale</MenuItem>
                  <MenuItem value="1">Text</MenuItem>
                </Select>
              </Stack>
              {isLinearScale && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(12, 1fr)",
                    gap: 1
                  }}>
                  <Box sx={{
                    gridColumn: "span 6"
                  }}>
                    <Stack>
                      <SmallInputLabel>Scale Points</SmallInputLabel>
                      <Select
                        disabled={isUpdatingSurveyQuestionType}
                        onChange={handleScalePointsChanged}
                        value={question.scalePoints?.toString()}
                      >
                        {[...Array(10)].map((_, index) => (
                          <MenuItem disabled={!index} key={index + 1} value={index + 1}>
                            {index + 1}
                          </MenuItem>
                        ))}
                      </Select>
                    </Stack>
                  </Box>
                  <Box sx={{
                    gridColumn: "span 6"
                  }}>
                    <Stack>
                      <SmallInputLabel>Include non applicable option</SmallInputLabel>
                      <Select
                        disabled={isUpdatingSurveyQuestionType}
                        defaultValue={question.includeNotApplicableOption?.toString()}
                        onChange={handleIncludeOptionChanged}
                      >
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                      </Select>
                    </Stack>
                  </Box>
                  <Box sx={{
                    gridColumn: "span 6"
                  }}>
                    <Stack>
                      <SmallInputLabel>Min Label</SmallInputLabel>
                      <TextField
                        disabled={isUpdatingSurveyQuestionType}
                        onBlur={handleMinLabelChanged}
                        defaultValue={question.minLabel || "Fully Disagree"}
                      />
                    </Stack>
                  </Box>
                  <Box sx={{
                    gridColumn: "span 6"
                  }}>
                    <Stack>
                      <SmallInputLabel>Max Label</SmallInputLabel>
                      <TextField
                        disabled={isUpdatingSurveyQuestionType}
                        onBlur={handleMaxLabelChanged}
                        defaultValue={question.maxLabel || "Fully Agree"}
                      />
                    </Stack>
                  </Box>
                  <Box sx={{
                    gridColumn: "span 12"
                  }}>
                    <Stack>
                      <SmallInputLabel>Question Group</SmallInputLabel>
                      <TagPicker
                        value={question.tags}
                        onChange={(value) => handleTagsChanged(value)}
                        tagType={TagType.SurveyQuestion}
                      />
                    </Stack>
                  </Box>
                </Box>
              )}
            </>
          )}
          <Divider />
          <Stack sx={{
            flexDirection: "row-reverse"
          }}>
            <Button variant="tertiary" color="error" onClick={handleDeleteClicked}>
              Delete Question
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </DrawerLayout>
  );
};

export { SurveyQuestionDrawerContent };
